import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Users, ChevronDown, ChevronUp, MessageCircle, Bell, Check, X, HelpCircle, Edit, Trash2, PartyPopper, FileText, ExternalLink, StickyNote, Link, Dumbbell } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const EventsList = ({
  events = [],
  players = [],
  onBack,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onSendReminder,
  onViewRSVPs,
  onOpenWhatsApp,
  onUpdateEventNotes
}) => {
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [showDocEmbed, setShowDocEmbed] = useState({});

  // Modal state for Admin Notes and Google Doc
  const [notesModalEvent, setNotesModalEvent] = useState(null);
  const [googleDocModalEvent, setGoogleDocModalEvent] = useState(null);
  const [adminNotesInput, setAdminNotesInput] = useState('');
  const [googleDocInput, setGoogleDocInput] = useState('');
  const [googleDocVisibleToPlayers, setGoogleDocVisibleToPlayers] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Convert Google Doc URL to embeddable URL
  const getEmbedUrl = (docUrl) => {
    if (!docUrl) return null;

    // Handle different Google Doc URL formats
    // Format 1: https://docs.google.com/document/d/DOCUMENT_ID/edit
    // Format 2: https://docs.google.com/document/d/DOCUMENT_ID/view
    const match = docUrl.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
    if (match) {
      const docId = match[1];
      return `https://docs.google.com/document/d/${docId}/preview`;
    }
    return null;
  };

  // Get RSVP counts for an event
  const getRSVPCounts = (event) => {
    const rsvps = event.rsvps || {};
    const counts = {
      yes: 0,
      no: 0,
      maybe: 0,
      pending: 0,
      total: 0
    };

    Object.values(rsvps).forEach(rsvp => {
      if (rsvp.status === 'yes') counts.yes++;
      else if (rsvp.status === 'no') counts.no++;
      else if (rsvp.status === 'maybe') counts.maybe++;
      else counts.pending++;
      counts.total++;
    });

    return counts;
  };

  // Get player name by ID
  const getPlayerName = (playerId) => {
    const player = players?.find(p => p.id === playerId || p.userId === playerId);
    return player?.name || 'Unknown';
  };

  // Format date nicely using shared helper
  const formatEventDate = (dateStr) => {
    return formatDate(dateStr, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Check if event is upcoming or past
  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    // Compare dates as strings (YYYY-MM-DD) to avoid timezone issues
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    return dateStr >= todayStr;
  };

  // Open Admin Notes modal
  const openNotesModal = (event) => {
    setAdminNotesInput(event.adminNotes || '');
    setNotesModalEvent(event);
  };

  // Open Google Doc modal
  const openGoogleDocModal = (event) => {
    setGoogleDocInput(event.googleDocLink || '');
    setGoogleDocVisibleToPlayers(event.googleDocVisibleToPlayers || false);
    setGoogleDocModalEvent(event);
  };

  // Save Admin Notes
  const handleSaveAdminNotes = async () => {
    if (!notesModalEvent || !onUpdateEventNotes) return;

    setSavingNotes(true);
    try {
      await onUpdateEventNotes(notesModalEvent.id, {
        adminNotes: adminNotesInput.trim()
      });
      setNotesModalEvent(null);
      setAdminNotesInput('');
    } catch (error) {
      console.error('Error saving admin notes:', error);
      alert('Error saving notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Save Google Doc
  const handleSaveGoogleDoc = async () => {
    if (!googleDocModalEvent || !onUpdateEventNotes) return;

    setSavingNotes(true);
    try {
      await onUpdateEventNotes(googleDocModalEvent.id, {
        googleDocLink: googleDocInput.trim() || null,
        googleDocVisibleToPlayers: googleDocVisibleToPlayers
      });
      setGoogleDocModalEvent(null);
      setGoogleDocInput('');
      setGoogleDocVisibleToPlayers(false);
    } catch (error) {
      console.error('Error saving Google Doc:', error);
      alert('Error saving Google Doc');
    } finally {
      setSavingNotes(false);
    }
  };

  // Sort events: upcoming first (by date), then past (by date desc)
  const sortedEvents = [...events].sort((a, b) => {
    const aUpcoming = isUpcoming(a.date);
    const bUpcoming = isUpcoming(b.date);

    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;

    if (aUpcoming) {
      return new Date(a.date) - new Date(b.date);
    } else {
      return new Date(b.date) - new Date(a.date);
    }
  });

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onBack} className="text-gray-600 mb-4 hover:text-gray-800">
        ← Back
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <PartyPopper className="text-purple-600" />
            Club Events
          </h2>
          <p className="text-gray-600 mt-1">Manage club events and track RSVPs</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onAddEvent({
              name: 'Leather Practice at Redmond Cow Corner',
              eventType: 'training',
              venue: '15115 NE 90th St, Redmond, WA 98052',
              description: 'Leather ball practice'
            })}
            className="bg-green-100 text-green-700 px-4 py-2 rounded-lg hover:bg-green-200 font-semibold flex items-center gap-2 border border-green-200"
          >
            <Dumbbell size={18} />
            + Practice Event
          </button>
          <button
            onClick={() => onAddEvent()}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 font-semibold flex items-center gap-2"
          >
            <Calendar size={18} />
            + Create Event
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {sortedEvents.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="text-purple-200 mb-4">
              <PartyPopper size={64} className="mx-auto" />
            </div>
            <p className="text-gray-500 text-lg mb-2">No events yet</p>
            <p className="text-gray-400 text-sm mb-6">Create your first club event to get started</p>
            <button
              onClick={onAddEvent}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold"
            >
              Create First Event
            </button>
          </div>
        ) : (
          sortedEvents.map(event => {
            const isExpanded = expandedEvent === event.id;
            const counts = getRSVPCounts(event);
            const upcoming = isUpcoming(event.date);

            return (
              <div
                key={event.id}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden ${!upcoming ? 'opacity-75' : ''
                  }`}
              >
                {/* Event Header */}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                    <div className="flex-1">
                      {/* Event Type Badge */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${event.eventType === 'social' ? 'bg-pink-100 text-pink-700' :
                          event.eventType === 'meeting' ? 'bg-blue-100 text-blue-700' :
                            event.eventType === 'training' ? 'bg-green-100 text-green-700' :
                              event.eventType === 'fundraiser' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-purple-100 text-purple-700'
                          }`}>
                          {event.eventType === 'social' ? '🎉 Social' :
                            event.eventType === 'meeting' ? '📋 Meeting' :
                              event.eventType === 'training' ? '🏃 Training' :
                                event.eventType === 'fundraiser' ? '💰 Fundraiser' :
                                  '📅 Event'}
                        </span>
                        {event.googleDocLink && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold flex items-center gap-1">
                            <FileText size={12} />
                            Notes
                          </span>
                        )}
                        {!upcoming && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-semibold">
                            Past
                          </span>
                        )}
                      </div>

                      {/* Event Name */}
                      <h3 className="font-bold text-lg sm:text-xl text-gray-900">{event.name}</h3>

                      {/* Event Details */}
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar size={16} className="text-purple-500" />
                          <span>{formatEventDate(event.date)}</span>
                        </div>

                        {event.time && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock size={16} className="text-purple-500" />
                            <span>{event.time}</span>
                            {event.endTime && <span>- {event.endTime}</span>}
                          </div>
                        )}

                        {event.venue && (
                          <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-purple-500" />
                            <span className="text-gray-600">{event.venue}</span>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-xs font-semibold"
                            >
                              <MapPin size={12} />
                              Maps
                            </a>
                          </div>
                        )}

                        {event.description && (
                          <p className="text-gray-600 text-sm mt-2">{event.description}</p>
                        )}
                      </div>

                      {/* RSVP Summary */}
                      <div className="mt-4 flex items-center gap-4">
                        <span className="text-sm font-semibold text-gray-700">RSVPs:</span>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 text-green-600">
                            <Check size={16} />
                            <span className="font-semibold">{counts.yes}</span>
                          </span>
                          <span className="flex items-center gap-1 text-red-600">
                            <X size={16} />
                            <span className="font-semibold">{counts.no}</span>
                          </span>
                          <span className="flex items-center gap-1 text-yellow-600">
                            <HelpCircle size={16} />
                            <span className="font-semibold">{counts.maybe}</span>
                          </span>
                          {counts.pending > 0 && (
                            <span className="text-gray-400 text-sm">
                              ({counts.pending} pending)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => onEditEvent(event)}
                          className="text-blue-600 hover:text-blue-800 p-2 border border-blue-600 rounded-lg hover:bg-blue-50"
                          title="Edit event"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete "${event.name}"? This cannot be undone.`)) {
                              onDeleteEvent(event.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg"
                          title="Delete event"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      {/* Admin Notes & Google Doc Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openNotesModal(event)}
                          className={`px-2 py-1.5 rounded text-xs sm:text-sm font-semibold flex items-center gap-1 ${event.adminNotes
                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          title={event.adminNotes ? "View/Edit admin notes" : "Add admin notes"}
                        >
                          <StickyNote size={14} />
                          {event.adminNotes ? 'Notes' : '+ Notes'}
                        </button>
                        <button
                          onClick={() => openGoogleDocModal(event)}
                          className={`px-2 py-1.5 rounded text-xs sm:text-sm font-semibold flex items-center gap-1 ${event.googleDocLink
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          title={event.googleDocLink ? "View/Edit Google Doc" : "Add Google Doc"}
                        >
                          <Link size={14} />
                          {event.googleDocLink ? 'Doc' : '+ Doc'}
                        </button>
                      </div>

                      {/* Reminder & WhatsApp Buttons */}
                      {upcoming && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => onSendReminder(event)}
                            className="px-2 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded text-xs sm:text-sm font-semibold flex items-center gap-1"
                            title="Send RSVP reminder"
                          >
                            <Bell size={14} />
                            Remind
                          </button>
                          <button
                            onClick={() => onOpenWhatsApp(event)}
                            className="px-2 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded text-xs sm:text-sm font-semibold flex items-center gap-1"
                            title="Send WhatsApp message"
                          >
                            <MessageCircle size={14} />
                            WhatsApp
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expandable RSVP Details */}
                <button
                  onClick={() => setExpandedEvent(isExpanded ? null : event.id)}
                  className="w-full px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Users size={16} />
                    View RSVP Details ({counts.total} responses)
                  </span>
                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {isExpanded && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Attending */}
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                          <Check size={16} />
                          Attending ({counts.yes})
                        </h4>
                        <div className="space-y-1">
                          {Object.entries(event.rsvps || {})
                            .filter(([_, r]) => r.status === 'yes')
                            .map(([playerId, rsvp]) => (
                              <div key={playerId} className="text-sm text-green-700 pb-1 border-b border-green-100 last:border-0">
                                <div>
                                  {getPlayerName(playerId)}
                                  {rsvp.guests > 0 && (
                                    <span className="text-green-500 ml-1">(+{rsvp.guests} guest{rsvp.guests > 1 ? 's' : ''})</span>
                                  )}
                                  {rsvp.mealPreference === 'veg' && <span className="ml-1" title="Veg">🥗</span>}
                                  {rsvp.mealPreference === 'non-veg' && <span className="ml-1" title="Non-Veg">🍗</span>}
                                  {rsvp.mealPreference === 'opt-out' && <span className="ml-1" title="Opt-Out">❌</span>}
                                </div>
                                {rsvp.note && <div className="text-xs text-green-600/80 italic mt-0.5 ml-2 break-words">"{rsvp.note}"</div>}
                              </div>
                            ))
                          }
                          {counts.yes === 0 && (
                            <p className="text-sm text-green-400 italic">No responses yet</p>
                          )}
                        </div>
                      </div>

                      {/* Not Attending */}
                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                          <X size={16} />
                          Not Attending ({counts.no})
                        </h4>
                        <div className="space-y-1">
                          {Object.entries(event.rsvps || {})
                            .filter(([_, r]) => r.status === 'no')
                            .map(([playerId, rsvp]) => (
                              <div key={playerId} className="text-sm text-red-700 pb-1 border-b border-red-100 last:border-0">
                                <div>
                                  {getPlayerName(playerId)}
                                </div>
                                {rsvp.note && <div className="text-xs text-red-600/80 italic mt-0.5 ml-2 break-words">"{rsvp.note}"</div>}
                              </div>
                            ))
                          }
                          {counts.no === 0 && (
                            <p className="text-sm text-red-400 italic">None</p>
                          )}
                        </div>
                      </div>

                      {/* Maybe */}
                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                          <HelpCircle size={16} />
                          Maybe ({counts.maybe})
                        </h4>
                        <div className="space-y-1">
                          {Object.entries(event.rsvps || {})
                            .filter(([_, r]) => r.status === 'maybe')
                            .map(([playerId, rsvp]) => (
                              <div key={playerId} className="text-sm text-yellow-700 pb-1 border-b border-yellow-100 last:border-0">
                                <div>
                                  {getPlayerName(playerId)}
                                  {rsvp.mealPreference === 'veg' && <span className="ml-1" title="Veg">🥗</span>}
                                  {rsvp.mealPreference === 'non-veg' && <span className="ml-1" title="Non-Veg">🍗</span>}
                                  {rsvp.mealPreference === 'opt-out' && <span className="ml-1" title="Opt-Out">❌</span>}
                                </div>
                                {rsvp.note && <div className="text-xs text-yellow-600/80 italic mt-0.5 ml-2 break-words">"{rsvp.note}"</div>}
                              </div>
                            ))
                          }
                          {counts.maybe === 0 && (
                            <p className="text-sm text-yellow-400 italic">None</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Notes section if any */}
                    {event.notes && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-700 text-sm mb-1">Admin Notes:</h4>
                        <p className="text-gray-600 text-sm">{event.notes}</p>
                      </div>
                    )}

                    {/* Google Doc Embed */}
                    {event.googleDocLink && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                            <FileText size={16} className="text-blue-600" />
                            Meeting Notes
                          </h4>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setShowDocEmbed(prev => ({
                                ...prev,
                                [event.id]: !prev[event.id]
                              }))}
                              className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-semibold"
                            >
                              {showDocEmbed[event.id] ? 'Hide Document' : 'Show Document'}
                            </button>
                            <a
                              href={event.googleDocLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 font-semibold flex items-center gap-1"
                            >
                              <ExternalLink size={12} />
                              Open in Google Docs
                            </a>
                          </div>
                        </div>

                        {showDocEmbed[event.id] && getEmbedUrl(event.googleDocLink) && (
                          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <iframe
                              src={getEmbedUrl(event.googleDocLink)}
                              width="100%"
                              height="500"
                              frameBorder="0"
                              title={`Meeting notes for ${event.name}`}
                              className="w-full"
                              style={{ minHeight: '500px' }}
                            />
                          </div>
                        )}

                        {showDocEmbed[event.id] && !getEmbedUrl(event.googleDocLink) && (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-700 text-sm">
                              ⚠️ Unable to embed this document. Please use the "Open in Google Docs" link to view.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Admin Notes Modal */}
      {notesModalEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <StickyNote className="text-yellow-600" size={24} />
                    Admin Notes
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    For: {notesModalEvent.name}
                  </p>
                </div>
                <button
                  onClick={() => setNotesModalEvent(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    🔒 These notes are only visible to club admins, not players.
                  </p>
                </div>
                <textarea
                  value={adminNotesInput}
                  onChange={(e) => setAdminNotesInput(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                  placeholder="Add internal notes about this event (budget, vendor contacts, logistics, etc.)..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setNotesModalEvent(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAdminNotes}
                  disabled={savingNotes}
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-semibold disabled:opacity-50"
                >
                  {savingNotes ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Doc Modal */}
      {googleDocModalEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="text-blue-600" size={24} />
                    Google Doc Link
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    For: {googleDocModalEvent.name}
                  </p>
                </div>
                <button
                  onClick={() => setGoogleDocModalEvent(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Google Doc URL
                  </label>
                  <input
                    type="url"
                    value={googleDocInput}
                    onChange={(e) => setGoogleDocInput(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://docs.google.com/document/d/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Make sure the doc is set to "Anyone with the link can view"
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={googleDocVisibleToPlayers}
                      onChange={(e) => setGoogleDocVisibleToPlayers(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-semibold text-gray-700">Visible to players</span>
                      <p className="text-sm text-gray-600">
                        Players can view this document in their event view
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setGoogleDocModalEvent(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGoogleDoc}
                  disabled={savingNotes}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
                >
                  {savingNotes ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsList;